<template>
  <BaseModal
    :model-value="modelValue"
    title="Set Signature"
    :title-logo="signatureIcon"
    width="640px"
    @update:model-value="$emit('update:modelValue', $event)"
    @close="handleClose"
  >
    <!-- Content -->
    <div>
      <p class="text-[14px] text-[#4a4a4a] leading-5 mb-4">
        Please draw your signature by hand in the draw box or upload your signature.
      </p>

      <!-- Signature Area -->
      <div
        class="signature-canvas-container bg-white border border-[#c4c4c4] rounded-[4px] relative"
        :class="{ 'border-[#004879]': isDrawing }"
      >
        <!-- Loading overlay -->
        <div
          v-if="isLoadingSignature"
          class="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center z-10"
        >
          <span class="text-[14px] text-[#4a4a4a]">Loading signature...</span>
        </div>

        <!-- Existing signature image -->
        <div
          v-if="existingSignatureUrl && !isDrawingMode"
          class="w-full h-[180px] flex items-center justify-center"
        >
          <img
            :src="existingSignatureUrl"
            alt="Existing signature"
            class="max-w-full max-h-full object-contain"
          />
        </div>

        <!-- Canvas for drawing new signature -->
        <canvas
          v-show="isDrawingMode"
          ref="canvasRef"
          class="signature-canvas w-full h-[180px] cursor-crosshair"
          @mousedown="startDrawing"
          @mousemove="draw"
          @mouseup="stopDrawing"
          @mouseleave="stopDrawing"
          @touchstart.prevent="startDrawingTouch"
          @touchmove.prevent="drawTouch"
          @touchend="stopDrawing"
        ></canvas>

        <!-- Undo and Clear buttons for canvas mode -->
        <div v-if="isDrawingMode && hasDrawnSignature && !isLoadingSignature" class="absolute bottom-2 right-3 flex items-center gap-3">
          <button
            :disabled="!canUndo"
            class="text-[14px] font-medium"
            :class="canUndo ? 'text-[#004879] hover:text-[#003a61] cursor-pointer underline' : 'text-[#c4c4c4] cursor-not-allowed'"
            @click="undoStroke"
          >
            Undo
          </button>
          <button
            class="text-[14px] font-medium text-[#004879] hover:text-[#003a61] cursor-pointer underline"
            @click="clearCanvas"
          >
            Clear
          </button>
        </div>

        <!-- Clear button for existing signature -->
        <div v-if="existingSignatureUrl && !isDrawingMode && !isLoadingSignature" class="absolute bottom-2 right-3">
          <button
            class="text-[14px] font-medium text-[#004879] hover:text-[#003a61] cursor-pointer underline"
            @click="clearExistingSignature"
          >
            Clear
          </button>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex items-center justify-between w-full">
        <NewButton
          type="normal"
          color-type="red-stroke"
          @click="handleClose"
        >
          Cancel
        </NewButton>
        <NewButton
          type="normal"
          color-type="dark-blue"
          :disabled="!hasSignature || isUploading"
          @click="handleSign"
        >
          {{ isUploading ? 'Signing...' : 'Sign' }}
        </NewButton>
      </div>
    </template>
  </BaseModal>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import BaseModal from './BaseModal.vue';
import NewButton from '@/NewLibrary/Button.vue';
import signatureIcon from '@/assets/encounterNotes/signature.svg';
import { getLatestSignatureIdApi, getSignatureApi, uploadSignatureApi, signEncounterNoteApi } from '@/api/encounterNotes';

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  customerId: {
    type: String,
    default: '',
  },
  noteId: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:modelValue', 'sign']);

const isLoadingSignature = ref(false);
const existingSignatureUrl = ref(null);
const existingSignatureId = ref(null); // Store the signature ID for signing
const isDrawingMode = ref(false); // true = show canvas, false = show existing image

const canvasRef = ref(null);
const isDrawing = ref(false);
const hasDrawnSignature = ref(false); // tracks if user has drawn on canvas
const canUndo = ref(false);
let ctx = null;
let lastX = 0;
let lastY = 0;
let strokeHistory = []; // Store canvas states for undo

// Has a valid signature (either existing or drawn)
const hasSignature = computed(() => {
  return (existingSignatureUrl.value && !isDrawingMode.value) || hasDrawnSignature.value;
});

const initCanvas = () => {
  if (!canvasRef.value) return;
  
  const canvas = canvasRef.value;
  const rect = canvas.getBoundingClientRect();
  
  // Set canvas size to match display size
  canvas.width = rect.width;
  canvas.height = rect.height;
  
  ctx = canvas.getContext('2d');
  ctx.strokeStyle = '#333';
  ctx.lineWidth = 2;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
};

const getMousePos = (e) => {
  const rect = canvasRef.value.getBoundingClientRect();
  return {
    x: e.clientX - rect.left,
    y: e.clientY - rect.top
  };
};

const getTouchPos = (e) => {
  const rect = canvasRef.value.getBoundingClientRect();
  const touch = e.touches[0];
  return {
    x: touch.clientX - rect.left,
    y: touch.clientY - rect.top
  };
};

const saveState = () => {
  if (!canvasRef.value) return;
  strokeHistory.push(canvasRef.value.toDataURL());
  canUndo.value = strokeHistory.length > 0;
};

const startDrawing = (e) => {
  // Save state before starting new stroke
  saveState();
  isDrawing.value = true;
  const pos = getMousePos(e);
  lastX = pos.x;
  lastY = pos.y;
};

const startDrawingTouch = (e) => {
  // Save state before starting new stroke
  saveState();
  isDrawing.value = true;
  const pos = getTouchPos(e);
  lastX = pos.x;
  lastY = pos.y;
};

const draw = (e) => {
  if (!isDrawing.value || !ctx) return;

  const pos = getMousePos(e);

  ctx.beginPath();
  ctx.moveTo(lastX, lastY);
  ctx.lineTo(pos.x, pos.y);
  ctx.stroke();

  lastX = pos.x;
  lastY = pos.y;
  hasDrawnSignature.value = true;
};

const drawTouch = (e) => {
  if (!isDrawing.value || !ctx) return;

  const pos = getTouchPos(e);

  ctx.beginPath();
  ctx.moveTo(lastX, lastY);
  ctx.lineTo(pos.x, pos.y);
  ctx.stroke();

  lastX = pos.x;
  lastY = pos.y;
  hasDrawnSignature.value = true;
};

const stopDrawing = () => {
  isDrawing.value = false;
};

const undoStroke = () => {
  if (strokeHistory.length === 0 || !ctx || !canvasRef.value) return;

  const previousState = strokeHistory.pop();
  canUndo.value = strokeHistory.length > 0;

  const img = new Image();
  img.onload = () => {
    ctx.clearRect(0, 0, canvasRef.value.width, canvasRef.value.height);
    ctx.drawImage(img, 0, 0);

    // Check if canvas is empty after undo
    const imageData = ctx.getImageData(0, 0, canvasRef.value.width, canvasRef.value.height);
    const hasContent = imageData.data.some((value, index) => index % 4 === 3 && value > 0);
    hasDrawnSignature.value = hasContent;
  };
  img.src = previousState;
};

const clearCanvas = () => {
  if (!ctx || !canvasRef.value) return;
  ctx.clearRect(0, 0, canvasRef.value.width, canvasRef.value.height);
  hasDrawnSignature.value = false;
  strokeHistory = [];
  canUndo.value = false;
};

// Clear existing signature and switch to drawing mode
const clearExistingSignature = async () => {
  existingSignatureUrl.value = null;
  isDrawingMode.value = true;
  await nextTick();
  initCanvas();
};

const loadExistingSignature = async () => {
  if (!props.customerId) return;

  isLoadingSignature.value = true;
  try {
    // First, get the latest signature ID
    const latestSignature = await getLatestSignatureIdApi(props.customerId);
    if (!latestSignature?.signature_id) {
      // No existing signature, switch to drawing mode
      isDrawingMode.value = true;
      await nextTick();
      initCanvas();
      return;
    }

    // Then, get the signature image details
    const signatureDetails = await getSignatureApi(latestSignature.signature_id);
    if (!signatureDetails?.download_url) {
      isDrawingMode.value = true;
      await nextTick();
      initCanvas();
      return;
    }

    // Show existing signature as image
    existingSignatureUrl.value = signatureDetails.download_url;
    existingSignatureId.value = signatureDetails.id;
    isDrawingMode.value = false;
  } catch (error) {
    console.error('Failed to load existing signature:', error);
    // Fallback to drawing mode on error
    isDrawingMode.value = true;
    await nextTick();
    initCanvas();
  } finally {
    isLoadingSignature.value = false;
  }
};

const handleClose = () => {
  emit('update:modelValue', false);
  resetState();
};

const isUploading = ref(false);

const handleSign = async () => {
  if (!hasSignature.value || !props.noteId) return;

  isUploading.value = true;
  try {
    let signatureId = null;

    // If using existing signature (not in drawing mode)
    if (existingSignatureUrl.value && !isDrawingMode.value) {
      signatureId = existingSignatureId.value;
    } else {
      // Drawing mode - upload new signature first
      if (!canvasRef.value) return;

      // Convert canvas to blob for upload
      const blob = await new Promise((resolve) => {
        canvasRef.value.toBlob(resolve, 'image/png');
      });

      // Upload signature if customerId is provided
      if (props.customerId && blob) {
        const uploadResult = await uploadSignatureApi(props.customerId, blob);
        signatureId = uploadResult?.id;
      }
    }

    // Call sign API with the signature ID
    if (signatureId) {
      const signResult = await signEncounterNoteApi(props.noteId, signatureId);
      emit('sign', { signatureId, signResult });
    }

    emit('update:modelValue', false);
    resetState();
  } catch (error) {
    console.error('Failed to sign:', error);
  } finally {
    isUploading.value = false;
  }
};

const resetState = () => {
  hasDrawnSignature.value = false;
  strokeHistory = [];
  canUndo.value = false;
  existingSignatureUrl.value = null;
  existingSignatureId.value = null;
  isDrawingMode.value = false;
};

// Initialize when modal opens
watch(
  () => props.modelValue,
  async (isOpen) => {
    if (isOpen) {
      // Load existing signature if available
      await loadExistingSignature();
    } else {
      resetState();
    }
  }
);

onMounted(() => {
  if (props.modelValue) {
    initCanvas();
  }
});
</script>

<style scoped>
.signature-canvas-container {
  overflow: hidden;
}

.signature-canvas {
  display: block;
  touch-action: none;
}
</style>
